import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';

export default function MagicLinkCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [error, setError] = useState<string | null>(null);
  // Prevent duplicate auth attempts due to effect re-runs
  const hasAttempted = useRef(false);

  useEffect(() => {
    const handleMagicLink = async () => {
      // Guard against duplicate API calls from effect re-runs
      if (hasAttempted.current) return;
      hasAttempted.current = true;

      const token = searchParams.get('token');

      if (!token) {
        setError('Invalid magic link - missing token');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      // Token should be 64 hex characters (32 bytes)
      if (token.length !== 64 || !/^[a-f0-9]+$/i.test(token)) {
        setError('Invalid magic link format');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        const response = await authAPI.verifyMagicLink(token);

        if (response.success && response.data) {
          // Store auth data
          setAuth(response.data.token, response.data.user);

          // Redirect based on new user status
          if (response.data.is_new_user) {
            navigate('/dashboard?welcome=true');
          } else {
            navigate('/dashboard');
          }
        } else {
          // Handle specific error codes
          if (response.error?.code === 'INVALID_TOKEN') {
            setError('This magic link has expired or already been used. Please request a new one.');
          } else {
            setError(response.error?.message || 'Failed to verify magic link');
          }
          setTimeout(() => navigate('/login'), 4000);
        }
      } catch (err) {
        console.error('Magic link verification error:', err);
        setError('An error occurred during sign in. Please try again.');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleMagicLink();
  }, [searchParams, navigate, setAuth]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-ondeck-50 to-ondeck-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Sign in failed
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">{error}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Redirecting to login...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-ondeck-50 to-ondeck-100 dark:from-gray-900 dark:to-gray-800">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ondeck-600 mx-auto mb-4"></div>
        <p className="text-gray-700 dark:text-gray-300">Verifying your magic link...</p>
      </div>
    </div>
  );
}
